import React, { useEffect, useState } from "react";
import { Button, Drawer, Form, Upload, useMessagedAsyncFn } from "@payping/web-components";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppTranslation } from "@/utils";
import { Close } from "@payping/icons";
import EditActionButtons from "@/components/mobile/settings/EditActionButtons";
import {
  DashboardChecksTypeEnum,
  FileTypeEnum,
  UserDocumentModel,
  apiConfigs,
  fileApis,
  uploadApis,
} from "@payping/api";
import { UploadChangeParam, UploadFile } from "@payping/web-components/components/Upload/types";
import { Upload as UploadIcon } from "@payping/icons/icons";
import { getFileName, getFileType } from "@payping/utils/string";
import { DashboardRoutes } from "@/router/constant";
import { isMobilePersonalRegisterURL } from "@/utils/isRegisterURL";
import { useHandleNavigationURL } from "@/mobile/Settings/Personal/utils";

const MobileEditPersonalDocuments: React.FC = () => {
  const isRegisterURL = isMobilePersonalRegisterURL();
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  const { settings } = DashboardRoutes;
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const checks = useAppSelector((state) => state.user.checks);
  const isPersonalDisabled = checks?.personalStatus === DashboardChecksTypeEnum.Confirmed;
  const documentsApi = useAppSelector((state) => state.user.personalInfo?.documents);
  const [, removeDocument] = useMessagedAsyncFn(
    (id) => fileApis.removeDocumentWithIdWithCancellationTokenDelete(id),
    [],
    undefined,
    {
      useProgress: true,
    },
  );

  const [documents, setDocuments] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (documentsApi) {
      setDocuments(
        documentsApi.map(({ serverName }: UserDocumentModel, index: number) => {
          const name = serverName || "file";
          return {
            name: `${getFileName(name)} ${index + 1}`,
            size: 0,
            type: getFileType(name),
            uid: String(index),
            url: name.replace("payping.ir", apiConfigs.domain),
          };
        }),
      );
    }
    return () => {
      setDocuments([]);
    };
  }, [documentsApi]);

  const [{ loading: loadingRemove }, onRemove] = useMessagedAsyncFn(async () => {
    if (documentsApi) {
      await Promise.all(
        documentsApi.map(async (doc: UserDocumentModel) => {
          await removeDocument(doc.id);
        }),
      );
    }
    navigate(settings.personal.root.url);
  });
  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async () => {
      if (documents && documents.length > 1) {
        await Promise.all(
          documents.map(async (doc: UploadFile) => {
            const fileName = doc.url ? doc.url.split("/").pop() : doc.response;
            if (fileName)
              await fileApis.addFileWithRequestModelWithCancellationTokenPost({
                fileName,
                type: FileTypeEnum.ShensanameKartMelli,
              });
          }),
        );
      }
      await setIsDrawerOpen(false);
      if (isRegisterURL) navigate(DashboardRoutes.register.personal.url);
      navigate(DashboardRoutes.settings.personal.root.url);
    },
    [documents],
    undefined,
    { useProgress: true },
  );

  // Custom Hooks
  const { handleNavigationURL } = useHandleNavigationURL();

  return (
    <>
      <Drawer
        open={isDrawerOpen}
        title={`${t("edit")} / ${t("settings.nationalIdAndCodePhoto")}`}
        height={"100%"}
        placement={"bottom"}
        closeIcon={false}
        extra={<Button type={"text"} icon={<Close width={14} height={14} />} onClick={handleNavigationURL} />}
      >
        <Form onFinish={onSubmit} form={form} initialValues={{ documents: documentsApi }}>
          <Form.Item name={"documents"}>
            <Upload
              crop={false}
              listType={"picture-card"}
              requestFunction={uploadApis.miscWithFilePost}
              onChange={(info: UploadChangeParam): void => setDocuments(info.fileList)}
              fileList={documents}
              multiple={true}
              maxCount={2}
              showUploadList={{ showPreviewIcon: true, showRemoveIcon: !isPersonalDisabled }}
              onRemove={onRemove}
              allowedExtensions={["jpg", "jpeg", "png"]}
              disabled={isPersonalDisabled}
            >
              {setDocuments.length < 2 && (
                <div>
                  <UploadIcon width={"20px"} />
                  <div className={"ant-upload-text"}>{t("profile.uploadNationalFiles")}</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Drawer>
      <EditActionButtons loading={loadingSubmit || loadingRemove} onAccept={form.submit} />
    </>
  );
};

export default MobileEditPersonalDocuments;
